// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Libs
import { DateTime } from 'luxon';
// Store & actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
// Components
import SubscriptionsTable from '@/app/modules/subscriptions/components/tables/SubscriptionsTable';
import Entity from '@/common-utils/components/renders/entity/Entity';
import DateTimePicker from '@/common-utils/components/renders/dateTimePicker/DateTimePicker';
import { ModalBody } from '@/common-utils/components/ui/modal';
// Common
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import subscriptionFields from '@/app/modules/subscriptions/fields/subscriptionFields';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';
// Lang
import { nsTerminal } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.step
 * @param root0.setDisabledSubmit
 * @param root0.setActionPayload
 * @returns {JSX} BodyMultipleBlockingBySelection
 */
function BodyMultipleBlockingBySelection({ step, setDisabledSubmit, setActionPayload }) {
    // Constants
    const MAX_STEPS_NUMBER = 3;
    const MAX_PICKS = 50;
    const STEP_TITLE_PREFIX = 'modal.multiple_blocking.by_selection.steps';

    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);

    // Constants
    const customQuery = {
        [subscriptionFields.PROVIDER.key]: rootOrganization.uuid,
        [generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.STATUS)]: 'ALLOWED_AND_TEMPORARILY_ALLOWED'
    };

    // State
    const [selectedSubscriptions, setSelectedSubscriptions] = useState(getInitialValueForRowSelected());
    const [immediateOptionSelected, setImmediateOptionSelected] = useState(true);
    const [dateTimeValues, setDateTimeValues] = useState(undefined);

    // Hooks
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: {
                stepTitle: `${STEP_TITLE_PREFIX}.select_subscription`,
                disabledNext: !Object.keys(selectedSubscriptions.rowIds).length
            },
            1: {
                stepTitle: `${STEP_TITLE_PREFIX}.select_blocking_mode`,
                disabledNext: !immediateOptionSelected && !dateTimeValues?.startDateSelected
            },
            2: {
                stepTitle: `${STEP_TITLE_PREFIX}.confirmation`,
                disabledNext: false
            }
        }),
        [selectedSubscriptions, immediateOptionSelected, dateTimeValues]
    );
    useEffect(() => {
        setDisabledSubmit(disabledNext);
    }, [step, disabledNext]);
    useEffect(() => {
        if (step === 2) {
            setActionPayload({
                posTerminalSubscriptionUuids: Object.keys(selectedSubscriptions.rowIds),
                startDate: immediateOptionSelected ? DateTime.utc().toISO() : dateTimeValues?.startDateSelected
            });
        }
    }, [step, selectedSubscriptions, immediateOptionSelected, dateTimeValues]);

    // Render
    return (
        <ModalBody className='modal-max-height overflow-auto'>
            <Entity
                entity={stepTitle}
                data={{ step: step + 1, maxStep: MAX_STEPS_NUMBER }}
                namespace={nsTerminal}
                className={'mb-2 flex'}
            />
            {step === 0 &&
                <SubscriptionsTable
                    filtersEventName={setFiltersEventName('subscriptions', true)}
                    customQuery={customQuery}
                    rowsSelected={selectedSubscriptions}
                    setRowsSelected={setSelectedSubscriptions}
                    maxPicks={MAX_PICKS}
                />
            }
            {step === 1 &&
                <div>
                    <div className='p-4 flex justify-center space-x-32'>
                        <div>
                            <label className='space-x-2'>
                                <input
                                    type='radio'
                                    checked={immediateOptionSelected}
                                    data-cy='radio-select-by-selection-multi-blocking'
                                    onChange={() => setImmediateOptionSelected(true)}
                                    title={'modal_date_range_picker_first_date'} />
                                <Entity entity='actions.row.block' namespace={nsTerminal} />
                            </label>
                        </div>
                        <div>
                            <label className='space-x-2'>
                                <input type='radio' value={'period'}
                                    checked={!immediateOptionSelected}
                                    data-cy='radio-select-by-csv-file-multi-blocking'
                                    onChange={() => setImmediateOptionSelected(false)} />
                                <Entity entity='actions.row.scheduled_block' namespace={nsTerminal} />
                            </label>
                        </div>
                    </div>
                    {!immediateOptionSelected &&
                        <div className='flex flex-col items-center'>
                            <DateTimePicker
                                setDateTimeValues={setDateTimeValues}
                                rangeDisabled={(date) => date <= new Date()}
                            />
                            {
                                dateTimeValues && dateTimeValues.startDateSelected &&
                                <Entity
                                    entity={'modal.blacklist.scheduled.summary'}
                                    data={{
                                        date: DateTime.fromISO(dateTimeValues.startDateSelected).toLocaleString(DateTime.DATE_SHORT),
                                        time: DateTime.fromISO(dateTimeValues.startDateSelected).toLocaleString(DateTime.TIME_SIMPLE)
                                    }}
                                    namespace={nsTerminal}
                                    className='text-base mt-4'
                                />
                            }
                        </div>
                    }
                </div>
            }
            {step === 2 &&
                <>
                    <div className='text-center'>
                        <Entity
                            entity='modal.multiple_blocking.by_selection.confirmation.text_1'
                            namespace={nsTerminal}
                            data={{ number: selectedSubscriptions.rowsData.length }}
                        />
                    </div>
                    {!immediateOptionSelected &&
                        <div className='text-center'>
                            <Entity
                                entity='modal.multiple_blocking.by_selection.confirmation.text_2'
                                namespace={nsTerminal}
                                data={{
                                    date: DateTime.fromISO(dateTimeValues.startDateSelected).toLocaleString(DateTime.DATE_SHORT),
                                    time: DateTime.fromISO(dateTimeValues.startDateSelected).toLocaleString(DateTime.TIME_SIMPLE)
                                }}
                            />
                        </div>
                    }
                    <div className='text-center mt-4'>
                        <Entity entity='modal.multiple_blocking.by_selection.confirmation.question' namespace={nsTerminal} />
                    </div>
                </>
            }
        </ModalBody>
    );
}

BodyMultipleBlockingBySelection.propTypes = {
    step: PropTypes.number,
    setDisabledSubmit: PropTypes.func,
    setActionPayload: PropTypes.func
};

export default BodyMultipleBlockingBySelection;